/**
 * Attachment caps are a UI hint. The ingest service still enforces its own
 * quota. Private cached hints belong to one owner, Auth SID and generation.
 */
import { useCallback, useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { executorClient } from "@/lib/executorDatabase";
import { FALLBACK_CAPS, parseCapsPayload, type ExecutorCapsState } from "@/lib/executorCaps";
import { createMirrorAuthSessionScope, type MirrorAuthLease } from "@/lib/mirrorAuthSession";

export type { ExecutorCaps, ExecutorCapsState } from "@/lib/executorCaps";
export { FALLBACK_CAPS, parseCapsPayload } from "@/lib/executorCaps";
export const EXECUTOR_CAPS_QUERY_KEY = "executor-caps";
const STALE_MS = 60_000;
const SESSION_CHANGED = "executor_caps_session_changed";

// All mounted consumers share the same cancellation identity and cache.
// The last consumer leaving revokes outstanding reads before a later mount.
const sessionScope = createMirrorAuthSessionScope();
let consumers = 0;
type CapsActor = Pick<MirrorAuthLease, "ownerId" | "authSessionId" | "generation">;
const actorOf = (): CapsActor | null => {
  const lease = sessionScope.capture();
  return lease ? { ownerId: lease.ownerId, authSessionId: lease.authSessionId, generation: lease.generation } : null;
};
const actorMatches = (actor: CapsActor | null, lease: MirrorAuthLease | null) =>
  !!actor && !!lease && actor.ownerId === lease.ownerId &&
  actor.authSessionId === lease.authSessionId && actor.generation === lease.generation;

export function useExecutorCaps() {
  const queryClient = useQueryClient();
  const [actor, setActor] = useState<CapsActor | null>(null);

  useEffect(() => {
    let alive = true;
    consumers++;
    const publish = () => { if (alive) setActor(actorOf()); };
    const observe = (session: unknown) => {
      if (!alive) return;
      const { changed } = sessionScope.observeSession(session);
      if (changed) queryClient.removeQueries({ queryKey: [EXECUTOR_CAPS_QUERY_KEY] });
      publish();
    };
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      // This callback stays synchronous; RPC/session reads run outside the auth lock.
      observe(session);
    });
    const revision = sessionScope.revision();
    void supabase.auth.getSession().then(({ data, error }) => {
      if (!alive) return;
      if (sessionScope.revision() !== revision) { publish(); return; }
      if (error) throw error;
      observe(data.session);
    }).catch(() => {
      if (alive && sessionScope.revision() === revision) {
        observe(null);
        console.error("[useExecutorCaps] session unavailable");
      }
    });
    return () => {
      alive = false;
      subscription.unsubscribe();
      consumers--;
      if (consumers === 0) {
        sessionScope.invalidate();
        queryClient.removeQueries({ queryKey: [EXECUTOR_CAPS_QUERY_KEY] });
      }
    };
  }, [queryClient]);

  const query = useQuery({
    queryKey: [EXECUTOR_CAPS_QUERY_KEY, actor?.ownerId ?? null, actor?.authSessionId ?? null, actor?.generation ?? null],
    enabled: !!actor,
    staleTime: STALE_MS,
    retry: (failures, error) => error.message !== SESSION_CHANGED && failures < 3,
    queryFn: async ({ signal }): Promise<ExecutorCapsState> => {
      const lease = sessionScope.capture();
      const assertCurrent = () => {
        if (signal.aborted || !lease || !actorMatches(actor, lease) || !sessionScope.isCurrent(lease))
          throw new Error(SESSION_CHANGED);
      };
      assertCurrent();
      const before = await supabase.auth.getSession();
      assertCurrent();
      if (before.error || !sessionScope.matchesSession(before.data.session, lease))
        throw new Error(SESSION_CHANGED);
      const { data, error } = await executorClient.rpc("executor_attachment_caps")
        .setHeader("Authorization", "Bearer " + lease.accessToken).abortSignal(signal);
      assertCurrent();
      const after = await supabase.auth.getSession();
      assertCurrent();
      if (after.error || !sessionScope.matchesSession(after.data.session, lease))
        throw new Error(SESSION_CHANGED);
      if (error) throw error;
      return parseCapsPayload(data);
    },
  });

  useEffect(() => {
    if (query.isError) console.error("[useExecutorCaps] rpc failed", query.error);
  }, [query.isError, query.error]);

  const invalidate = useCallback(() => {
    void queryClient.invalidateQueries({
      queryKey: actor ? [EXECUTOR_CAPS_QUERY_KEY, actor.ownerId] : [EXECUTOR_CAPS_QUERY_KEY],
    });
  }, [queryClient, actor]);
  const visible = actorMatches(actor, sessionScope.capture());
  const state: ExecutorCapsState = visible && !query.isError ? query.data ?? FALLBACK_CAPS : FALLBACK_CAPS;

  return { ...state, loading: !visible || query.isPending, invalidate, refresh: query.refetch };
}
